<template>
  <div class="cliente-selector">
    <v-row align="center" no-gutters>
      <v-col cols="10">
        <v-autocomplete
          v-model="selectedCustomerId"
          :items="customers"
          item-title="name"
          item-value="id"
          label="Cliente *"
          placeholder="Buscar por nombre o RUT..."
          :rules="[v => !!v || 'Cliente es requerido']"
          :loading="loading"
          clearable
          variant="outlined"
          density="compact"
          :no-data-text="customers.length === 0 ? 'No hay clientes registrados' : 'No se encontraron clientes'"
          @update:model-value="onCustomerSelected"
          :custom-filter="customFilter"
        >
          <template v-slot:item="{ props, item }">
            <v-list-item v-bind="props">
              <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
              <v-list-item-subtitle>
                RUT: {{ item.raw.tax_id || 'No especificado' }} | 
                Email: {{ item.raw.contact_email || item.raw.email || 'No especificado' }}
              </v-list-item-subtitle>
            </v-list-item>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="2" class="pl-2">
        <v-btn
          color="primary"
          icon="mdi-plus"
          @click="showCreateCustomerDialog = true"
          size="default"
          :disabled="loading"
        ></v-btn>
      </v-col>
    </v-row>

    <!-- Dialog para crear nuevo cliente -->
    <v-dialog v-model="showCreateCustomerDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Nuevo Cliente</span>
        </v-card-title>
        
        <v-card-text>
          <v-form ref="customerForm" v-model="customerFormValid">
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="newCustomer.name"
                    label="Nombre o Razón Social *"
                    :rules="[v => !!v || 'Nombre es requerido']"
                    required
                    variant="outlined"
                    density="compact"
                  ></v-text-field>
                </v-col>
                
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newCustomer.tax_id"
                    label="RUT o Identificación Tributaria"
                    placeholder="12.345.678-9"
                    variant="outlined"
                    density="compact"
                  ></v-text-field>
                </v-col>
                
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newCustomer.contact_person"
                    label="Persona de Contacto"
                    variant="outlined"
                    density="compact"
                  ></v-text-field>
                </v-col>
                
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newCustomer.contact_phone"
                    label="Teléfono"
                    type="tel"
                    variant="outlined"
                    density="compact"
                  ></v-text-field>
                </v-col>
                
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newCustomer.contact_email"
                    label="Email"
                    type="email"
                    variant="outlined"
                    density="compact"
                    :rules="emailRules"
                  ></v-text-field>
                </v-col>
                
                <v-col cols="12">
                  <v-textarea
                    v-model="newCustomer.address"
                    label="Dirección"
                    rows="2"
                    variant="outlined"
                    density="compact"
                  ></v-textarea>
                </v-col>
                
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newCustomer.city"
                    label="Ciudad/Comuna"
                    variant="outlined"
                    density="compact"
                  ></v-text-field>
                </v-col>
                
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newCustomer.postal_code"
                    label="Código Postal"
                    variant="outlined"
                    density="compact"
                  ></v-text-field>
                </v-col>
                
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newCustomer.payment_terms"
                    label="Condiciones de Pago"
                    placeholder="30 días, contado, etc."
                    variant="outlined"
                    density="compact"
                  ></v-text-field>
                </v-col>
                
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="newCustomer.credit_limit"
                    label="Límite de Crédito"
                    type="number"
                    prefix="$"
                    variant="outlined"
                    density="compact"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeCreateCustomerDialog">
            Cancelar
          </v-btn>
          <v-btn 
            color="primary" 
            @click="createCustomer"
            :loading="creatingCustomer"
            :disabled="!customerFormValid"
          >
            Crear Cliente
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para notificaciones -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
    >
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn
          color="white"
          variant="text"
          @click="snackbar.show = false"
        >
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import api from '@/api'

export default {
  name: 'ClienteSelector',
  props: {
    modelValue: {
      type: [Number, String],
      default: null
    },
    required: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'customer-selected', 'customer-created'],
  
  data() {
    return {
      customers: [],
      selectedCustomerId: this.modelValue,
      loading: false,
      showCreateCustomerDialog: false,
      creatingCustomer: false,
      customerFormValid: false,
      newCustomer: {
        name: '',
        tax_id: '',
        contact_person: '',
        contact_phone: '',
        contact_email: '',
        address: '',
        city: '',
        postal_code: '',
        payment_terms: '',
        credit_limit: 0
      },
      emailRules: [
        v => !v || /.+@.+\..+/.test(v) || 'Email debe ser válido'
      ],
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 4000
      }
    }
  },
  
  watch: {
    modelValue(newValue) {
      this.selectedCustomerId = newValue
    },
    
    selectedCustomerId(newValue) {
      this.$emit('update:modelValue', newValue)
    }
  },
  
  async created() {
    await this.loadCustomers()
  },
  
  methods: {
    async loadCustomers() {
      try {
        this.loading = true
        const response = await api.get('/egresos/customers')
        this.customers = response.data || []
        console.log('Customers loaded:', this.customers.length)
      } catch (error) {
        console.error('Error loading customers:', error)
        this.showError('Error al cargar clientes')
      } finally {
        this.loading = false
      }
    },
    
    onCustomerSelected(customerId) {
      const customer = this.customers.find(c => c.id === customerId)
      this.$emit('customer-selected', customer)
    },
    
    customFilter(itemText, queryText, item) {
      if (!queryText) return true
      
      const query = queryText.toString().toLowerCase()
      const name = item.raw.name.toLowerCase()
      const taxId = (item.raw.tax_id || '').toLowerCase()
      const email = (item.raw.contact_email || item.raw.email || '').toLowerCase()
      
      return name.includes(query) || taxId.includes(query) || email.includes(query)
    },
    
    async createCustomer() {
      if (!this.$refs.customerForm.validate()) {
        return
      }
      
      try {
        this.creatingCustomer = true
        const response = await api.post('/egresos/customers', this.newCustomer)
        
        if (response.data && response.data.customer) {
          // Agregar el nuevo cliente a la lista
          this.customers.push(response.data.customer)
          
          // Seleccionar el nuevo cliente
          this.selectedCustomerId = response.data.customer.id
          this.$emit('customer-created', response.data.customer)
          
          this.showSuccess('Cliente creado exitosamente')
          this.closeCreateCustomerDialog()
        }
      } catch (error) {
        console.error('Error creating customer:', error)
        const errorMessage = error.response?.data?.error || 'Error al crear cliente'
        this.showError(errorMessage)
      } finally {
        this.creatingCustomer = false
      }
    },
    
    closeCreateCustomerDialog() {
      this.showCreateCustomerDialog = false
      this.resetCustomerForm()
    },
    
    resetCustomerForm() {
      this.newCustomer = {
        name: '',
        tax_id: '',
        contact_person: '',
        contact_phone: '',
        contact_email: '',
        address: '',
        city: '',
        postal_code: '',
        payment_terms: '',
        credit_limit: 0
      }
      this.customerFormValid = false
      if (this.$refs.customerForm) {
        this.$refs.customerForm.resetValidation()
      }
    },
    
    showSuccess(message) {
      this.snackbar = {
        show: true,
        message: message,
        color: 'success',
        timeout: 4000
      }
    },
    
    showError(message) {
      this.snackbar = {
        show: true,
        message: message,
        color: 'error',
        timeout: 6000
      }
    }
  }
}
</script>

<style scoped>
.cliente-selector {
  width: 100%;
}

.v-autocomplete {
  margin-bottom: 0;
}

.v-btn {
  margin-left: 8px;
}
</style> 